import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Activity as ActivityIcon, Search } from 'lucide-react'
import { JarvisCard } from '@/components/JarvisCard'
import { ActivityItem } from '@/components/ActivityItem'
import { useActivity } from '@/lib/hooks'
import type { ActivityItem as ActivityItemType } from '@/types'

function bucket(item: ActivityItemType): string {
  const date = new Date(item.timestamp)
  const today = new Date()
  const yesterday = new Date(Date.now() - 86_400_000)

  if (date.toDateString() === today.toDateString()) return 'Today'
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
}

export function ActivityLog() {
  const [filter, setFilter] = useState<'' | ActivityItemType['type']>('')
  const [query, setQuery] = useState('')
  const { data, isLoading } = useActivity({ q: query, type: filter })

  const grouped = useMemo(() => {
    const map = new Map<string, ActivityItemType[]>()
    for (const item of data?.items ?? []) {
      const key = bucket(item)
      const list = map.get(key) ?? []
      list.push(item)
      map.set(key, list)
    }
    return Array.from(map.entries())
  }, [data])

  const hasFilters = Boolean(filter || query.trim())

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight">Activity Log</h1>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as ActivityItemType['type'] | '')}
            className="bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-jarvis-blue/60"
          >
            <option value="">All types</option>
            <option value="call">Calls</option>
            <option value="task">Tasks</option>
            <option value="briefing">Briefings</option>
            <option value="ads_sync">Ads syncs</option>
          </select>
          <div className="flex items-center gap-2 bg-jarvis-surface-2 border border-jarvis-border rounded-lg px-3 py-1.5 text-sm">
            <Search size={14} className="text-jarvis-muted" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="bg-transparent focus:outline-none placeholder:text-jarvis-muted"
            />
          </div>
        </div>
      </div>

      {isLoading && (
        <JarvisCard>
          <div className="text-jarvis-muted text-sm py-8 text-center">Loading…</div>
        </JarvisCard>
      )}

      {grouped.map(([label, items]) => (
        <JarvisCard key={label}>
          <h2 className="text-xs uppercase tracking-widest text-jarvis-muted mb-2">
            {label}
          </h2>
          {items.map((item) => (
            <ActivityItem key={`${item.type}-${item.timestamp}-${item.title}`} item={item} />
          ))}
        </JarvisCard>
      ))}

      {!isLoading && grouped.length === 0 && (
        <JarvisCard>
          <div className="text-center py-10 px-4">
            <ActivityIcon size={28} className="mx-auto text-jarvis-muted/60 mb-3" />
            {hasFilters ? (
              <>
                <p className="text-sm">No activity matches your filters.</p>
                <button
                  onClick={() => {
                    setFilter('')
                    setQuery('')
                  }}
                  className="mt-3 text-xs text-jarvis-blue hover:underline"
                >
                  Clear filters
                </button>
              </>
            ) : (
              <>
                <p className="text-sm">No activity yet.</p>
                <p className="text-xs text-jarvis-muted mt-1">
                  Calls, tasks, and briefings will appear here as Jarvis works.
                </p>
                <div className="mt-4 flex gap-2 justify-center">
                  <Link
                    to="/setup"
                    className="text-xs px-3 py-1.5 rounded-md bg-jarvis-blue text-jarvis-surface hover:bg-jarvis-blue-bright transition-colors"
                  >
                    Connect a provider
                  </Link>
                  <Link
                    to="/settings#briefing"
                    className="text-xs px-3 py-1.5 rounded-md border border-jarvis-border hover:bg-jarvis-surface-2 transition-colors"
                  >
                    Set up briefings
                  </Link>
                </div>
              </>
            )}
          </div>
        </JarvisCard>
      )}
    </div>
  )
}
